import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CHART_COLORS,
  WorldMapChart,
  type CountryDataItem,
} from 'app/modules/analytics/components/shared/charts';
import type { CountryLegendItem } from 'app/modules/analytics/components/shared/charts/components/CountryLegend';
import AnalyticsExportPageFrame, {
  type AnalyticsExportPageProfile,
} from './AnalyticsExportPageFrame';
import { analyticsExportTheme as theme } from './analyticsExportTheme';

interface Props {
  title: string;
  subtitle: string;
  mapData: CountryDataItem[];
  legendItems: CountryLegendItem[];
  profile: AnalyticsExportPageProfile;
  pageNumber: number;
  totalPages: number;
}

const AnalyticsExportAudienceLocationPage: FC<Props> = ({
  title,
  subtitle,
  mapData,
  legendItems,
  profile,
  pageNumber,
  totalPages,
}) => {
  const { t } = useTranslation();

  return (
    <AnalyticsExportPageFrame
      title={title}
      subtitle={subtitle}
      profile={profile}
      pageNumber={pageNumber}
      totalPages={totalPages}
      dataCy="analytics_export-public_audience-location-page"
    >
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-items-stretch tw-gap-4 tw-py-8">
        <div
          className={`${theme.tile} tw-flex tw-w-[64mm] tw-shrink-0 tw-flex-col tw-gap-4 tw-self-start tw-rounded-2xl tw-px-6 tw-py-5`}
        >
          <span className={`tw-text-lg ${theme.tileLabel}`}>
            {t('analyticsExports.topCountries')}
          </span>
          <ul className="tw-flex tw-flex-col tw-gap-3">
            {legendItems.map((item) => (
              <li
                key={item.countryCode}
                className={`tw-text-base ${theme.tileValue}`}
              >
                {`${item.label} ${item.percent}%`}
              </li>
            ))}
          </ul>
        </div>
        <div
          className={`${theme.chartSurface} tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-px-6 tw-py-4`}
        >
          <WorldMapChart
            className="tw-min-h-0 tw-flex-1"
            data={mapData}
            zoomable={false}
            showScaleLegend={false}
            height="100%"
            baseColor={CHART_COLORS.mapHoverBase}
            highlightColor={CHART_COLORS.defaultDark}
          />
        </div>
      </div>
    </AnalyticsExportPageFrame>
  );
};

export default AnalyticsExportAudienceLocationPage;
